import { create } from "zustand";
import { api } from "../services/api";
import type { UserResponse, UserLogin, UserRegister, TokenResponse, TokenVerifyRequest, ForgotPasswordRequest, ResetPasswordRequest } from "../types";

interface AuthState {
  user: UserResponse | null;
  isAuthenticated: boolean;
  loading: boolean;
  initializing: boolean;
  error: string | null;
  login: (credentials: UserLogin, rememberMe?: boolean) => Promise<void>;
  loginWithTokens: (tokens: TokenResponse) => Promise<void>;
  register: (data: UserRegister) => Promise<{ email: string }>;
  verifyEmailToken: (data: TokenVerifyRequest) => Promise<void>;
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;
  logout: () => void;
  clearError: () => void;
  initialize: () => Promise<void>;
  updateUser: (updated: UserResponse) => void;
  refreshUser: () => Promise<void>;
}

const getTokenStorage = (): Storage =>
  localStorage.getItem("access_token") ? localStorage : sessionStorage;

// Só há sessão a validar (e, portanto, splash a exibir) se existir um token
// guardado. Visitantes novos renderizam a landing imediatamente — sem flash de
// spinner no caminho do LCP e permitindo hidratação limpa do HTML pré-renderizado.
const hasStoredToken = (): boolean =>
  typeof window !== "undefined" &&
  !!(localStorage.getItem("access_token") || sessionStorage.getItem("access_token"));

export const useAuthStore = create<AuthState>((set, get) => {
  if (typeof window !== "undefined") {
    window.addEventListener("auth-logout", () => {
      get().logout();
    });
  }

  const _storeSession = (data: TokenResponse, user: UserResponse, rememberMe: boolean) => {
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem("access_token", data.access_token);
    storage.setItem("refresh_token", data.refresh_token);
    storage.setItem("user", JSON.stringify(user));
  };

  return {
    user: null,
    isAuthenticated: false,
    loading: false,
    initializing: hasStoredToken(),
    error: null,

    login: async (credentials, rememberMe = true) => {
      set({ loading: true, error: null });
      try {
        const tokenRes = await api.post<TokenResponse>("/api/auth/login", credentials);
        const meRes = await api.get<UserResponse>("/api/auth/me", {
          headers: { Authorization: `Bearer ${tokenRes.data.access_token}` },
        });

        if (rememberMe) {
          localStorage.setItem("saved_email", credentials.email);
        } else {
          localStorage.removeItem("saved_email");
        }

        _storeSession(tokenRes.data, meRes.data, rememberMe);
        set({ user: meRes.data, isAuthenticated: true, loading: false });
      } catch (err: any) {
        set({ error: err.response?.data?.detail || "Ocorreu um erro ao fazer login.", loading: false });
        throw err;
      }
    },

    loginWithTokens: async (tokens) => {
      // Usado pelo retorno do login social (Google/Microsoft): já recebemos os
      // tokens do backend, então só buscamos o perfil e persistimos a sessão.
      set({ loading: true, error: null });
      try {
        const meRes = await api.get<UserResponse>("/api/auth/me", {
          headers: { Authorization: `Bearer ${tokens.access_token}` },
        });
        _storeSession(tokens, meRes.data, true);
        set({ user: meRes.data, isAuthenticated: true, loading: false });
      } catch (err: any) {
        set({ error: err.response?.data?.detail || "Não foi possível concluir o login social.", loading: false });
        throw err;
      }
    },

    register: async (data) => {
      set({ loading: true, error: null });
      try {
        const res = await api.post<{ message: string; email: string }>("/api/auth/register", data);
        set({ loading: false });
        return { email: res.data.email };
      } catch (err: any) {
        set({ error: err.response?.data?.detail || "Erro ao iniciar cadastro.", loading: false });
        throw err;
      }
    },

    verifyEmailToken: async (data) => {
      set({ loading: true, error: null });
      try {
        await api.post("/api/auth/verify-email", data);
        set({ loading: false });
      } catch (err: any) {
        set({ error: err.response?.data?.detail || "Link inválido ou expirado.", loading: false });
        throw err;
      }
    },

    forgotPassword: async (data) => {
      set({ loading: true, error: null });
      try {
        await api.post("/api/auth/forgot-password", data);
        set({ loading: false });
      } catch (err: any) {
        set({ error: err.response?.data?.detail || "Erro ao solicitar redefinição de senha.", loading: false });
        throw err;
      }
    },

    resetPassword: async (data) => {
      set({ loading: true, error: null });
      try {
        await api.post("/api/auth/reset-password", data);
        set({ loading: false });
      } catch (err: any) {
        set({ error: err.response?.data?.detail || "Link inválido ou expirado.", loading: false });
        throw err;
      }
    },

    logout: () => {
      ["access_token", "refresh_token", "user"].forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      set({ user: null, isAuthenticated: false, error: null });
    },

    clearError: () => set({ error: null }),

    updateUser: (updated: UserResponse) => {
      getTokenStorage().setItem("user", JSON.stringify(updated));
      set({ user: updated });
    },

    refreshUser: async () => {
      try {
        const response = await api.get<UserResponse>("/api/auth/me");
        getTokenStorage().setItem("user", JSON.stringify(response.data));
        set({ user: response.data });
      } catch {
        // Mantém o usuário atual em caso de falha transitória.
      }
    },

    initialize: async () => {
      const accessToken = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
      const savedUser = localStorage.getItem("user") || sessionStorage.getItem("user");

      if (!accessToken) {
        set({ isAuthenticated: false, initializing: false });
        return;
      }

      try {
        if (savedUser) {
          set({ user: JSON.parse(savedUser), isAuthenticated: true });
        }
        const response = await api.get<UserResponse>("/api/auth/me");
        getTokenStorage().setItem("user", JSON.stringify(response.data));
        set({ user: response.data, isAuthenticated: true, initializing: false });
      } catch {
        ["access_token", "refresh_token", "user"].forEach((key) => {
          localStorage.removeItem(key);
          sessionStorage.removeItem(key);
        });
        set({ user: null, isAuthenticated: false, initializing: false });
      }
    },
  };
});
